const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export const apiClient = {
  // Auth APIs
  sendCode: async (phoneNumber, recaptchaToken) => {
    const response = await fetch(`${API_URL}/api/auth/send-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ phoneNumber, recaptchaToken }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to send code');
    }
    return response.json(); // Returns { message, sessionInfo }
  },

  verifyCode: async (sessionInfo, code) => {
    const response = await fetch(`${API_URL}/api/auth/verify-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ sessionInfo, code }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to verify code');
    }
    return response.json(); // Returns { verified, userExists, user, token, phoneNumber, firebaseUid }
  },

  signup: async (phoneNumber, name, firebaseUid) => {
    const response = await fetch(`${API_URL}/api/users`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ phoneNumber, name, firebaseUid }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create account');
    }
    return response.json();
  },

  // User APIs
  getCurrentUser: async (userId) => {
    const response = await fetch(`${API_URL}/api/users/me`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get user');
    }
    return response.json();
  },

  getAllUsers: async () => {
    const response = await fetch(`${API_URL}/api/users`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get users');
    }
    return response.json();
  },

  updateUser: async (userId, data) => {
    const response = await fetch(`${API_URL}/api/users/update`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId, ...data }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update user');
    }
    return response.json();
  },

  updateUserRole: async (userId, role) => {
    const response = await fetch(`${API_URL}/api/users/update-role`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId, role }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update user role');
    }
    return response.json();
  },

  deleteUser: async (userId) => {
    const response = await fetch(`${API_URL}/api/users/delete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete user');
    }
    return response.json();
  },

  // Church APIs
  createChurch: async (name, address, requesterUserId, initialAdminIds) => {
    const response = await fetch(`${API_URL}/api/churches`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ name, address, requesterUserId, initialAdminIds }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create church');
    }
    return response.json();
  },

  getAllChurches: async () => {
    const response = await fetch(`${API_URL}/api/churches`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get churches');
    }
    return response.json();
  },

  getChurch: async (churchId) => {
    const response = await fetch(`${API_URL}/api/churches/${churchId}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get church');
    }
    return response.json();
  },

  updateChurch: async (churchId, name, address, requesterUserId) => {
    const response = await fetch(`${API_URL}/api/churches/${churchId}/update`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ name, address, requesterUserId }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update church');
    }
    return response.json();
  },

  uploadChurchLogo: async (churchId, logoFile, requesterUserId) => {
    const formData = new FormData();
    formData.append('logo', logoFile);
    formData.append('requesterUserId', requesterUserId);

    const response = await fetch(`${API_URL}/api/churches/${churchId}/upload-logo`, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to upload logo');
    }
    return response.json();
  },

  addChurchAdmin: async (churchId, requesterUserId, adminUserId) => {
    const response = await fetch(`${API_URL}/api/churches/${churchId}/add-admin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ requesterUserId, adminUserId }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to add church admin');
    }
    return response.json();
  },

  removeChurchAdmin: async (churchId, requesterUserId, adminUserId) => {
    const response = await fetch(`${API_URL}/api/churches/${churchId}/remove-admin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ requesterUserId, adminUserId }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to remove church admin');
    }
    return response.json();
  },

  getChurchAdmins: async (churchId) => {
    const response = await fetch(`${API_URL}/api/churches/${churchId}/admins`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get church admins');
    }
    return response.json();
  },

  deactivateChurch: async (churchId, requesterUserId) => {
    const response = await fetch(`${API_URL}/api/churches/${churchId}/deactivate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ requesterUserId }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to deactivate church');
    }
    return response.json();
  },

  joinChurch: async (churchId, userId) => {
    const response = await fetch(`${API_URL}/api/churches/${churchId}/join`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to join church');
    }
    return response.json();
  },

  getMyChurches: async (userId) => {
    const response = await fetch(`${API_URL}/api/churches/my-churches`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get my churches');
    }
    return response.json();
  },

  // User Display Settings APIs
  getUserDisplaySettings: async (churchId) => {
    const response = await fetch(`${API_URL}/api/churches/${churchId}/user-display-settings`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get user display settings');
    }
    return response.json();
  },

  updateUserDisplaySettings: async (churchId, requesterUserId, quoteOfTheDay) => {
    const response = await fetch(`${API_URL}/api/churches/${churchId}/user-display-settings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ requesterUserId, quoteOfTheDay }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update user display settings');
    }
    return response.json();
  },

  uploadBannerImage: async (churchId, requesterUserId, bannerFile) => {
    const formData = new FormData();
    formData.append('banner', bannerFile);
    formData.append('requesterUserId', requesterUserId);

    const response = await fetch(`${API_URL}/api/churches/${churchId}/user-display-settings/upload-banner`, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to upload banner image');
    }
    return response.json();
  },

  deleteBannerImage: async (churchId, requesterUserId, bannerUrl) => {
    const response = await fetch(`${API_URL}/api/churches/${churchId}/user-display-settings/delete-banner`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ requesterUserId, bannerUrl }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete banner image');
    }
    return response.json();
  },

  // Announcement APIs
  createAnnouncement: async (churchId, title, content, userId) => {
    const response = await fetch(`${API_URL}/api/announcements`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ churchId, title, content, userId }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create announcement');
    }
    return response.json();
  },

  getAnnouncements: async (churchId) => {
    const response = await fetch(`${API_URL}/api/announcements/get`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ churchId }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get announcements');
    }
    return response.json();
  },

  updateAnnouncement: async (announcementId, title, content, userId) => {
    const response = await fetch(`${API_URL}/api/announcements/${announcementId}/update`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ title, content, userId }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update announcement');
    }
    return response.json();
  },

  deleteAnnouncement: async (announcementId, userId) => {
    const response = await fetch(`${API_URL}/api/announcements/${announcementId}/delete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete announcement');
    }
    return response.json();
  },

  // Sermon APIs
  createSermon: async (churchId, sermonData, userId) => {
    const response = await fetch(`${API_URL}/api/sermons`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ...sermonData, churchId, userId }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create sermon');
    }
    return response.json();
  },

  getSermons: async (churchId) => {
    const response = await fetch(`${API_URL}/api/sermons/get`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ churchId }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get sermons');
    }
    return response.json();
  },

  updateSermon: async (sermonId, sermonData, userId) => {
    const response = await fetch(`${API_URL}/api/sermons/${sermonId}/update`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ...sermonData, userId }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update sermon');
    }
    return response.json();
  },

  deleteSermon: async (sermonId, userId) => {
    const response = await fetch(`${API_URL}/api/sermons/${sermonId}/delete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete sermon');
    }
    return response.json();
  },

  // Attendance APIs
  checkIn: async (userId, churchId) => {
    const response = await fetch(`${API_URL}/api/attendance/check-in`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId, churchId }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to check in');
    }
    return response.json();
  },

  getAttendance: async (churchId, startDate = null, endDate = null, userId = null, requesterRole = null) => {
    const body = { churchId };
    if (startDate) body.startDate = startDate;
    if (endDate) body.endDate = endDate;
    if (userId) body.userId = userId;
    if (requesterRole) body.requesterRole = requesterRole;

    const response = await fetch(`${API_URL}/api/attendance/get`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get attendance');
    }
    return response.json();
  },

  // Prayer Request APIs
  createPrayerRequest: async (churchId, userId, title, message) => {
    const response = await fetch(`${API_URL}/api/prayer-requests`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ churchId, userId, title, message }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create prayer request');
    }
    return response.json();
  },

  getPrayerRequests: async (churchId, userId, requesterRole) => {
    const response = await fetch(`${API_URL}/api/prayer-requests/get`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ churchId, userId, requesterRole }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get prayer requests');
    }
    return response.json();
  },

  updatePrayerRequestStatus: async (requestId, requesterUserId, status) => {
    const response = await fetch(`${API_URL}/api/prayer-requests/${requestId}/update-status`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ requesterUserId, status }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update prayer request status');
    }
    return response.json();
  },

  // Payment APIs
  createPayment: async (userId, churchId, amount, paymentMethod = 'cash') => {
    const response = await fetch(`${API_URL}/api/payments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId, churchId, amount, paymentMethod }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create payment');
    }
    return response.json();
  },

  getPayments: async (churchId, filterDate = null, userId = null, requesterRole = null) => {
    const body = { churchId };
    if (filterDate) body.filterDate = filterDate;
    if (userId) body.userId = userId;
    if (requesterRole) body.requesterRole = requesterRole;

    const response = await fetch(`${API_URL}/api/payments/get`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get payments');
    }
    return response.json();
  },

  // Post APIs
  createPost: async (churchId, userId, content, imageFile) => {
    const formData = new FormData();
    formData.append('churchId', churchId);
    formData.append('userId', userId);
    formData.append('content', content);
    if (imageFile) {
      formData.append('image', imageFile);
    }

    const response = await fetch(`${API_URL}/api/posts`, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create post');
    }
    return response.json();
  },

  getPosts: async (churchId) => {
    const response = await fetch(`${API_URL}/api/posts/get`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ churchId }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get posts');
    }
    return response.json();
  },

  likePost: async (postId, userId) => {
    const response = await fetch(`${API_URL}/api/posts/${postId}/like`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to like post');
    }
    return response.json();
  },
};
